import { SxProps, Theme } from "@mui/material";

export const containerStyle: SxProps<Theme> = (theme) => ({
  display: "flex",
  flexDirection: "column",
  flex: 1,
  minHeight: 0,
  width: "100%",
  maxWidth: "100%",
  overflow: "hidden",
  color: theme.palette.quizPopap.quizText,
});

export const headerStyle: SxProps<Theme> = {
  px: { xs: 2, sm: 3, md: 3 },
  pb: { xs: 2, sm: 3, md: 3 },
  pt: 0,
  display: "flex",
  flexDirection: "column",
  gap: { xs: 2, md: 1.5 },
};

export const metricsRowStyle: SxProps<Theme> = {
  display: "grid",
  gridTemplateColumns: { xs: "auto 1fr", md: "auto 1fr auto" },
  gap: { xs: 1.5, md: 2 },
  alignItems: { xs: "start", md: "center" },
};

export const progressBlockStyle: SxProps<Theme> = {
  minWidth: 0,
  alignSelf: { xs: "center", md: "auto" },
};

export const progressHeaderRowStyle: SxProps<Theme> = {
  display: "flex",
  justifyContent: "space-between",
  gap: 1,
  mb: 0.75,
};

export const progressLabelStyle: SxProps<Theme> = {
  color: "#06B6D4",
  fontSize: { xs: "14px", md: "16px" },
  fontWeight: 600,
};

export const linearProgressStyle: SxProps<Theme> = (theme) => ({
  height: 10,
  borderRadius: 999,
  backgroundColor: theme.palette.quizPopap.quizLineBg,
  "& .MuiLinearProgress-bar": {
    background: "linear-gradient(90deg, #8957CF 0%, #28C9DC 100%)",
  },
});

export const headerCirclesStyle: SxProps<Theme> = {
  display: { xs: "none", sm: "none", md: "flex" },
  gap: { xs: 2, md: 3 },
  alignItems: "center",
  flexWrap: "wrap",
  justifyContent: { xs: "space-between", md: "flex-start" },
};

export const contentStyle: SxProps<Theme> = {
  flex: 1,
  minHeight: 0,
  overflowY: "auto",
  WebkitOverflowScrolling: "touch",
  px: { xs: 2, sm: 3, md: 3 },
  pb: { xs: 3, md: 1.5 },
};

export const winnersSectionStyle: SxProps<Theme> = {
  position: "relative",
  borderRadius: "24px",
  px: { xs: 1, sm: 2, md: 2.5 },
  py: { xs: 2.5, sm: 3, md: 2 },
  overflow: "hidden",
};

export const confettiOverlayStyle: SxProps<Theme> = {
  position: "absolute",
  inset: 0,
  pointerEvents: "none",
  opacity: 0.65,
  backgroundImage:
    "radial-gradient(circle at 10% 20%, rgba(40, 201, 220, 0.32) 0 2px, transparent 2px)," +
    "radial-gradient(circle at 25% 75%, rgba(251, 191, 36, 0.38) 0 2px, transparent 2px)," +
    "radial-gradient(circle at 40% 30%, rgba(137, 87, 207, 0.24) 0 2px, transparent 2px)," +
    "radial-gradient(circle at 70% 65%, rgba(40, 201, 220, 0.28) 0 2px, transparent 2px)," +
    "radial-gradient(circle at 86% 22%, rgba(251, 191, 36, 0.32) 0 2px, transparent 2px)," +
    "radial-gradient(circle at 92% 82%, rgba(137, 87, 207, 0.22) 0 2px, transparent 2px)",
};

export const winnersInnerStyle: SxProps<Theme> = {
  position: "relative",
  zIndex: 1,
};

export const winnersTitleStyle: SxProps<Theme> = (theme) => ({
  textAlign: "center",
  fontSize: { xs: "30px", md: "32px" },
  lineHeight: 1.05,
  fontWeight: 800,
  color: theme.palette.quizPopap.quizText,
});

export const winnersSubtitleStyle: SxProps<Theme> = (theme) => ({
  mt: { xs: 1, md: 0.5 },
  textAlign: "center",
  fontSize: { xs: "14px", md: "14px" },
  color: theme.palette.quizPopap.quizText,
  opacity: 0.72,
});

export const resultsStateBadgeStyle = (
  finished: boolean
): SxProps<Theme> => (theme) => {
  const isDark = theme.palette.mode === "dark";

  return {
    mt: 1.25,
    mx: "auto",
    px: 1.5,
    py: 0.5,
    width: "fit-content",
    borderRadius: "999px",
    fontSize: "12px",
    fontWeight: 700,
    lineHeight: 1.1,
    color: finished
      ? isDark
        ? "#86EFAC"
        : "#166534"
      : isDark
        ? "#67E8F9"
        : "#0E7490",
    backgroundColor: finished
      ? isDark
        ? "rgba(34, 197, 94, 0.20)"
        : "rgba(34, 197, 94, 0.14)"
      : isDark
        ? "rgba(6, 182, 212, 0.22)"
        : "rgba(6, 182, 212, 0.14)",
    border: `1px solid ${
      finished
        ? isDark
          ? "rgba(34, 197, 94, 0.45)"
          : "rgba(34, 197, 94, 0.28)"
        : isDark
          ? "rgba(6, 182, 212, 0.45)"
          : "rgba(6, 182, 212, 0.28)"
    }`,
  };
};

export const podiumGridStyle: SxProps<Theme> = {
  mt: { xs: 4, md: 3 },
  display: "grid",
  gridTemplateColumns: { xs: "1fr", md: "repeat(3, minmax(0, 1fr))" },
  gap: { xs: 2, md: 1.5 },
  alignItems: "end",
};

export const podiumItemWrapStyle = (highlighted: boolean): SxProps<Theme> => ({
  position: "relative",
  paddingBottom: { xs: "32px", md: "20px" },
  transform: { xs: "none", md: highlighted ? "translateY(-8px)" : "none" },
});

export const podiumCardStyle = (
  place: number,
  highlighted: boolean,
): SxProps<Theme> => ({
  position: "relative",
  zIndex: 2,
  minHeight: { xs: place === 1 ? 196 : 168, md: place === 1 ? 156 : 138 },
  borderRadius: "24px 24px 18px 18px",
  border: `1px solid ${highlighted ? "rgba(251, 191, 36, 0.42)" : "rgba(15, 23, 42, 0.10)"}`,
  backgroundColor: "#FFFFFF",
  boxShadow: highlighted
    ? "0 20px 40px rgba(251, 191, 36, 0.14)"
    : "0 14px 32px rgba(15, 23, 42, 0.08)",
  px: { xs: 2.5, md: 1.5 },
  pt: { xs: 3, md: 1.75 },
  pb: { xs: 4, md: 2.5 },
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  justifyContent: "center",
});

export const podiumBaseWrapStyle: SxProps<Theme> = (theme) => ({
  position: "absolute",
  left: { xs: -6, md: -10 },
  right: { xs: -6, md: -10 },
  bottom: 0,
  height: { xs: 38, md: 34 },
  width: "auto",
  overflow: "hidden",
  display: "flex",
  alignItems: "flex-end",
  justifyContent: "center",
  pointerEvents: "none",
  zIndex: 0,
  "&::after": {
    content: '""',
    position: "absolute",
    left: 0,
    right: 0,
    bottom: 0,
    height: { xs: 10, md: 12 },
    background:
      theme.palette.mode === "dark"
        ? "transparent"
        : "linear-gradient(180deg, rgba(255, 255, 255, 0) 0%, rgba(248, 252, 255, 0.88) 72%, rgba(255, 255, 255, 0.96) 100%)",
    pointerEvents: "none",
  },
});

export const podiumBaseStyle: SxProps<Theme> = {
  width: "100%",
  height: "100%",
  display: "block",
  objectFit: "fill",
};

export const podiumBadgeStyle = (place: number): SxProps<Theme> => ({
  width: { xs: place === 1 ? 56 : 48, md: place === 1 ? 48 : 42 },
  height: { xs: place === 1 ? 56 : 48, md: place === 1 ? 48 : 42 },
  borderRadius: "50%",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  fontSize: { xs: place === 1 ? "24px" : "20px", md: place === 1 ? "20px" : "17px" },
  fontWeight: 800,
  color: place === 1 ? "#B45309" : place === 2 ? "#6B7280" : "#C2410C",
  background:
    place === 1
      ? "linear-gradient(180deg, #FEF3C7 0%, #FBBF24 100%)"
      : place === 2
        ? "linear-gradient(180deg, #F3F4F6 0%, #9CA3AF 100%)"
        : "linear-gradient(180deg, #FED7AA 0%, #FB923C 100%)",
  boxShadow:
    place === 1
      ? "0 10px 22px rgba(251, 191, 36, 0.24)"
      : place === 2
        ? "0 10px 22px rgba(148, 163, 184, 0.22)"
        : "0 10px 22px rgba(251, 146, 60, 0.20)",
});

export const podiumNameStyle: SxProps<Theme> = {
  mt: { xs: 2, md: 1.25 },
  fontSize: { xs: "20px", md: "21px" },
  fontWeight: 700,
  color: "#0F172A",
  textAlign: "center",
};

export const podiumPointsStyle: SxProps<Theme> = {
  mt: 0.5,
  fontSize: { xs: "18px", md: "24px" },
  fontWeight: 800,
  color: "#06B6D4",
  textAlign: "center",
};

export const summaryCardsGridStyle: SxProps<Theme> = {
  mt: { xs: 4, md: 2.5 },
  display: "grid",
  gridTemplateColumns: { xs: "1fr", md: "repeat(3, minmax(0, 1fr))" },
  gap: { xs: 2, md: 1.25 },
};

export const summaryCardStyle: SxProps<Theme> = {
  display: "flex",
  gap: { xs: 1.5, md: 1.25 },
  alignItems: "center",
  borderRadius: "20px",
  border: "1px solid rgba(15, 23, 42, 0.10)",
  backgroundColor: "#FFFFFF",
  boxShadow: "0 10px 24px rgba(15, 23, 42, 0.06)",
  p: { xs: 2, md: 1.25 },
};

export const summaryIconWrapStyle = (
  background: string,
  color: string,
): SxProps<Theme> => ({
  width: 44,
  height: 44,
  borderRadius: "50%",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  background,
  color,
  flexShrink: 0,
});

export const summaryTextWrapStyle: SxProps<Theme> = {
  minWidth: 0,
};

export const summaryTitleStyle: SxProps<Theme> = {
  fontSize: { xs: "14px", md: "13px" },
  fontWeight: 700,
  color: "#0F172A",
};

export const summaryNameStyle: SxProps<Theme> = {
  fontSize: { xs: "20px", md: "18px" },
  fontWeight: 700,
  color: "#0F172A",
  lineHeight: 1.15,
};

export const summaryMetaStyle: SxProps<Theme> = {
  fontSize: { xs: "14px", md: "13px" },
  fontWeight: 500,
  color: "rgba(15, 23, 42, 0.70)",
};

export const areaInsightsContainerStyle: SxProps<Theme> = {
  mt: { xs: 2.5, md: 2 },
  borderRadius: "20px",
  border: "1px solid rgba(15, 23, 42, 0.10)",
  backgroundColor: "#FFFFFF",
  boxShadow: "0 10px 24px rgba(15, 23, 42, 0.06)",
  overflow: "hidden",
};

export const areaInsightsGridStyle: SxProps<Theme> = {
  display: "grid",
  gridTemplateColumns: { xs: "1fr", md: "repeat(2, minmax(0, 1fr))" },
};

export const areaInsightCardStyle = (
  variant: "strongest" | "needsReview",
): SxProps<Theme> => ({
  display: "flex",
  gap: { xs: 1.5, md: 1.75 },
  alignItems: "flex-start",
  p: { xs: 2, md: 2.25 },
  backgroundColor:
    variant === "strongest"
      ? "rgba(34, 197, 94, 0.08)"
      : "rgba(14, 165, 233, 0.08)",
  borderBottom: {
    xs: variant === "strongest" ? "1px solid rgba(15, 23, 42, 0.08)" : "none",
    md: "none",
  },
  borderRight: {
    xs: "none",
    md:
      variant === "strongest" ? "1px solid rgba(15, 23, 42, 0.08)" : "none",
  },
});

export const areaInsightIconWrapStyle = (
  variant: "strongest" | "needsReview",
): SxProps<Theme> => ({
  width: 44,
  height: 44,
  borderRadius: "50%",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  flexShrink: 0,
  backgroundColor:
    variant === "strongest"
      ? "rgba(34, 197, 94, 0.16)"
      : "rgba(14, 165, 233, 0.16)",
  color: variant === "strongest" ? "#16A34A" : "#0284C7",
});

export const areaInsightTextWrapStyle: SxProps<Theme> = {
  minWidth: 0,
  flex: 1,
};

export const areaInsightLabelStyle = (
  variant: "strongest" | "needsReview",
): SxProps<Theme> => ({
  fontSize: { xs: "14px", md: "13px" },
  fontWeight: 700,
  color: variant === "strongest" ? "#16A34A" : "#0284C7",
});

export const areaInsightTitleStyle: SxProps<Theme> = {
  mt: 0.35,
  fontSize: { xs: "20px", md: "18px" },
  fontWeight: 700,
  color: "#0F172A",
  lineHeight: 1.2,
};

export const areaInsightMetaStyle: SxProps<Theme> = {
  mt: 0.5,
  fontSize: { xs: "14px", md: "13px" },
  fontWeight: 500,
  color: "rgba(15, 23, 42, 0.70)",
  lineHeight: 1.35,
};

export const positionCardStyle: SxProps<Theme> = (theme) => ({
  mt: { xs: 2.5, md: 1.75 },
  display: "flex",
  gap: { xs: 1.5, md: 1.25 },
  alignItems: "center",
  borderRadius: "20px",
  border: "1px solid rgba(6, 182, 212, 0.18)",
  background:
    theme.palette.mode === "dark"
      ? "linear-gradient(180deg, rgba(6, 182, 212, 0.16) 0%, rgba(6, 182, 212, 0.06) 100%)"
      : "linear-gradient(180deg, rgba(6, 182, 212, 0.08) 0%, rgba(255, 255, 255, 1) 100%)",
  p: { xs: 2, md: 1.25 },
});

export const positionIconWrapStyle: SxProps<Theme> = {
  width: 44,
  height: 44,
  borderRadius: "50%",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  backgroundColor: "rgba(6, 182, 212, 0.14)",
  color: "#06B6D4",
  flexShrink: 0,
};

export const positionTitleStyle: SxProps<Theme> = (theme) => ({
  fontSize: { xs: "15px", md: "14px" },
  fontWeight: 700,
  color: theme.palette.quizPopap.quizText,
});

export const positionMetaStyle: SxProps<Theme> = (theme) => ({
  fontSize: { xs: "20px", md: "18px" },
  fontWeight: 700,
  color: theme.palette.quizPopap.quizText,
});

export const positionScoreStyle: SxProps<Theme> = {
  color: "#06B6D4",
};

export const scoreBreakdownCardStyle: SxProps<Theme> = (theme) => ({
  mt: { xs: 2, md: 1.5 },
  display: "flex",
  flexDirection: "column",
  gap: 0.75,
  borderRadius: "20px",
  border: "1px solid rgba(6, 182, 212, 0.18)",
  background:
    theme.palette.mode === "dark"
      ? "linear-gradient(180deg, rgba(6, 182, 212, 0.16) 0%, rgba(6, 182, 212, 0.06) 100%)"
      : "linear-gradient(180deg, rgba(6, 182, 212, 0.08) 0%, rgba(255, 255, 255, 1) 100%)",
  p: { xs: 2, md: 1.5 },
});

export const scoreBreakdownRowStyle: SxProps<Theme> = (theme) => ({
  fontSize: { xs: "14px", md: "13px" },
  fontWeight: 500,
  color:
    theme.palette.mode === "dark"
      ? "rgba(248, 250, 252, 0.82)"
      : "#0F172A",
  lineHeight: 1.35,
  display: "flex",
  justifyContent: "space-between",
  alignItems: "baseline",
  gap: 1.5,
});

export const scoreBreakdownValueStyle: SxProps<Theme> = {
  color: "#06B6D4",
  fontWeight: 700,
  fontSize: { xs: "14px", md: "13px" },
  whiteSpace: "nowrap",
};

export const footerStyle: SxProps<Theme> = (theme) => ({
  p: { xs: 2, md: 1.5 },
  borderTop: `1px solid ${theme.palette.quizPopap.quizSeparator}`,
  display: "flex",
  justifyContent: "center",
});

export const footerActionsStyle: SxProps<Theme> = {
  display: "flex",
  gap: { xs: 2, md: 1.25 },
  flexWrap: "wrap",
  justifyContent: "center",
};

export const primaryActionStyle: SxProps<Theme> = {
  minWidth: { xs: 220, md: 190 },
  px: { xs: 3, md: 2.5 },
  py: { xs: 1.2, md: 1 },
  borderRadius: "999px",
  textTransform: "none",
  fontWeight: 700,
  color: "#FFFFFF",
  background: "linear-gradient(90deg, #28C9DC 0%, #06B6D4 100%)",
  "&:hover": {
    background: "linear-gradient(90deg, #28C9DC 0%, #06B6D4 100%)",
    opacity: 0.92,
  },
};

export const secondaryActionStyle: SxProps<Theme> = (theme) => ({
  minWidth: { xs: 220, md: 190 },
  px: { xs: 3, md: 2.5 },
  py: { xs: 1.2, md: 1 },
  borderRadius: "999px",
  textTransform: "none",
  fontWeight: 700,
  color: theme.palette.quizPopap.quizText,
  borderColor: theme.palette.quizPopap.quizSeparator,
});

export const questionFeedbackSectionStyle: SxProps<Theme> = {
  mt: { xs: 2.5, md: 3 },
  width: "100%",
  textAlign: "left",
};

export const questionFeedbackToggleButtonStyle: SxProps<Theme> = (theme) => ({
  width: "100%",
  justifyContent: "center",
  gap: 1,
  px: 2,
  py: 1.25,
  borderRadius: "20px",
  textTransform: "none",
  fontSize: { xs: "15px", md: "16px" },
  fontWeight: 700,
  color: theme.palette.quizPopap.quizText,
  backgroundColor: theme.palette.quizPopap.quizSelector,
  border: `1px solid ${theme.palette.quizPopap.quizSeparator}`,
  "&:hover": {
    backgroundColor: theme.palette.quizPopap.quizSelector,
    opacity: 0.92,
  },
});

export const questionFeedbackToggleIconStyle = (
  isOpen: boolean,
): SxProps<Theme> => ({
  display: "inline-flex",
  alignItems: "center",
  transition: "transform 0.2s ease",
  transform: isOpen ? "rotate(180deg)" : "rotate(0deg)",
});

export const questionFeedbackListStyle: SxProps<Theme> = {
  display: "flex",
  flexDirection: "column",
  gap: 1.5,
  mt: 1.5,
};

export const questionFeedbackItemStyle: SxProps<Theme> = (theme) => ({
  p: { xs: 1.5, md: 2 },
  borderRadius: "12px",
  border: `1px solid ${theme.palette.quizPopap.quizSeparator}`,
  backgroundColor: theme.palette.quizPopap.quizInsideBg,
  display: "flex",
  flexDirection: "column",
  gap: 1.25,
});

export const questionFeedbackLabelRowStyle: SxProps<Theme> = {
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  gap: 1,
  flexWrap: "wrap",
};

export const questionFeedbackMetaStyle: SxProps<Theme> = (theme) => ({
  fontSize: "12px",
  fontWeight: 700,
  color: theme.palette.quizPopap.quizText,
  opacity: 0.7,
});

export const questionFeedbackScoreBadgeStyle = (
  isCorrect: boolean,
): SxProps<Theme> => ({
  fontSize: "12px",
  fontWeight: 700,
  color: isCorrect ? "#15803D" : "#B91C1C",
  backgroundColor: isCorrect
    ? "rgba(34, 197, 94, 0.15)"
    : "rgba(239, 68, 68, 0.12)",
  border: isCorrect
    ? "1px solid rgba(34, 197, 94, 0.35)"
    : "1px solid rgba(239, 68, 68, 0.35)",
  borderRadius: "8px",
  px: 1,
  py: 0.35,
  flexShrink: 0,
});

export const questionFeedbackQuestionStyle: SxProps<Theme> = (theme) => ({
  fontSize: { xs: "14px", md: "15px" },
  fontWeight: 600,
  lineHeight: 1.45,
  color: theme.palette.quizPopap.quizText,
});

export const questionFeedbackAnswerLabelStyle: SxProps<Theme> = (theme) => ({
  fontSize: "13px",
  fontWeight: 500,
  color: theme.palette.quizPopap.quizText,
  opacity: 0.65,
});

export const questionFeedbackAnswerBoxStyle: SxProps<Theme> = (theme) => ({
  padding: "12px 14px",
  borderRadius: "8px",
  backgroundColor: "rgba(148, 163, 184, 0.12)",
  border: `1px solid ${theme.palette.quizPopap.quizSeparator}`,
  fontSize: "13px",
  fontWeight: 500,
  color: theme.palette.quizPopap.quizText,
  whiteSpace: "pre-wrap",
  lineHeight: 1.5,
});

export const questionFeedbackJustificationStyle = (
  isCorrect: boolean,
): SxProps<Theme> => (theme) => ({
  mt: 0.25,
  p: 1.5,
  borderRadius: "8px",
  backgroundColor: isCorrect ? "#66EF64B2" : "#DD503E",
  color: isCorrect ? theme.palette.quizPopap.quizText : "#FFFFFF",
  fontSize: "13px",
  fontWeight: 500,
  lineHeight: 1.5,
  whiteSpace: "pre-wrap",
});
